import axios from 'axios';

/** Default draw-to-text placeholder — unedited layers are dropped before template save. */
export const DESIGN_STATE_PLACEHOLDER_TEXT = 'Add text here';

export const isBase64DataUrl = (value: unknown): value is string =>
    typeof value === 'string' && value.startsWith('data:');

export const dataUrlToBlob = (dataUrl: string): Blob => {
    const [header, base64Data] = dataUrl.split(',');
    const mimeMatch = header.match(/data:(.*?);base64/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const binary = atob(base64Data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mime });
};

export const stripPlaceholderTextAnnotations = (
    annotations: Record<string, unknown> | null | undefined,
) => {
    if (!annotations || typeof annotations !== 'object') {
        return { annotations, removed: 0 };
    }

    const cleaned: Record<string, unknown> = {};
    let removed = 0;

    for (const [id, ann] of Object.entries(annotations)) {
        const textAnn = ann as { name?: string; text?: string } | null;
        if (
            textAnn &&
            textAnn.name === 'Text' &&
            typeof textAnn.text === 'string' &&
            textAnn.text.trim() === DESIGN_STATE_PLACEHOLDER_TEXT
        ) {
            removed += 1;
            continue;
        }
        cleaned[id] = ann;
    }

    return { annotations: cleaned, removed };
};

export type DesignStateSaveAnalysis = {
    needsBgUpload: boolean;
    placeholdersRemoved: number;
    cleanedAnnotations: Record<string, unknown> | null | undefined;
    needsSave: boolean;
};

/** Preview what `prepareDesignStateForSave` would change (used by backfill dry-run). */
export const analyzeDesignStateForSave = (
    designState: Record<string, unknown> | null | undefined,
): DesignStateSaveAnalysis => {
    if (!designState) {
        return {
            needsBgUpload: false,
            placeholdersRemoved: 0,
            cleanedAnnotations: designState,
            needsSave: false,
        };
    }

    const needsBgUpload = isBase64DataUrl(designState.imgSrc);
    const { annotations, removed } = stripPlaceholderTextAnnotations(
        designState.annotations as Record<string, unknown> | undefined,
    );

    return {
        needsBgUpload,
        placeholdersRemoved: removed,
        cleanedAnnotations: annotations,
        needsSave: needsBgUpload || removed > 0,
    };
};

export const uploadDataUrlToS3 = async (
    dataUrl: string,
    baseUrl: string,
): Promise<string> => {
    const blob = dataUrlToBlob(dataUrl);
    const ext = blob.type === 'image/png' ? 'png' : 'jpg';
    const formData = new FormData();
    formData.append('file', blob, `${Math.random()}_bg.${ext}`);
    formData.append('id', '0');
    formData.append('type', 'promotions');
    const res = await axios.post(`${baseUrl}s3/uploadwithtype`, formData);
    return res.data;
};

export type PreparedDesignState = {
    designState: Record<string, unknown>;
    placeholdersRemoved: number;
};

/**
 * Normalize design state before persisting a template:
 * - drop unedited "Add text here" text layers
 * - upload base64 imgSrc to S3 and store URL (falls back to base64 if upload fails)
 */
export const prepareDesignStateForSave = async (
    designState: Record<string, unknown> | null | undefined,
    baseUrl: string,
): Promise<PreparedDesignState> => {
    if (!designState) {
        return { designState: designState as Record<string, unknown>, placeholdersRemoved: 0 };
    }

    const { annotations, removed } = stripPlaceholderTextAnnotations(
        designState.annotations as Record<string, unknown> | undefined,
    );

    let next: Record<string, unknown> =
        removed > 0 ? { ...designState, annotations } : { ...designState };

    if (isBase64DataUrl(next.imgSrc)) {
        try {
            const bgUrl = await uploadDataUrlToS3(next.imgSrc as string, baseUrl);
            if (bgUrl && !isBase64DataUrl(bgUrl)) {
                next = { ...next, imgSrc: bgUrl };
            }
        } catch {
            // Fall back to base64 imgSrc when upload fails.
        }
    }

    return { designState: next, placeholdersRemoved: removed };
};
